import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import { Button } from "@sellar-io/components";
import dayjs from "dayjs";
import { ReactElement, useState } from "react";
import { useNavigate } from "react-router";
import { useOrderDocumentList } from "~/hooks/api/v2/orderDocument/useOrderDocumentList";
import { useOrderInvoiceUrl } from "../../../hooks/api/v2/order/useOrderInvoiceUrl";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { useBuildStripeDashboardUrl } from "../../../utils/stripeDashboardUrl";
import { BuyerOnly } from "~/components/authFilter/BuyerOnly";
import { SupplierOnly } from "~/components/authFilter/SupplierOnly";
import { OrderContentConnectionsType } from "../../../pages/order/OrderContent/OrderContent";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../modal/Dialog";
import { InvoiceStatusRow } from "./StatusRow";
import { PAYMENT_LINK_REF } from "../../../constants/routes";

const DATE_FORMAT = "DD/MM/YY HH:mm";

type Props = {
  order: OrderGetResponseType;
  onVatInvoiceClick?: () => void;
  connection?: OrderContentConnectionsType;
};

export const ImplicitProformaInvoiceStatus = ({
  order,
  onVatInvoiceClick,
  connection,
}: Props): ReactElement | null => {
  const { business } = useSellarCurrentBusiness();
  const isBuyer = !!business?.Retailer;
  const isSupplier = !!business?.Supplier;
  const buildStripeDashboardUrl = useBuildStripeDashboardUrl();
  const navigate = useNavigate();
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);

  const isSellarCredit = order.paymentType === "sellarCredit";
  const isSellarImport = order.paymentType === "cardPaymentWithDeposit";
  const paymentLinkCode = order.PaymentLink?.code ?? null;
  const paymentTerms = connection?.paymentTerms;
  const showPaymentBeforeDispatchWarning =
    paymentTerms !== "credit" && paymentTerms !== "paymentOnDelivery";

  // Fetch Sellar Credit invoice document (only when needed; React Query deduplicates
  // if ImplicitProformaPaymentStatus issues the same query in the same render).
  const orderDocumentQuery = useOrderDocumentList(
    {
      limit: 50,
      query: [
        isSupplier && {
          type: "SupplierId",
          modifier: "EQUALS",
          value: business?.Supplier?.id,
        },
        isBuyer && {
          type: "RetailerId",
          modifier: "EQUALS",
          value: business?.Retailer?.id,
        },
        { type: "OrderId", modifier: "EQUALS", value: order.id },
      ].filter(Boolean),
    },
    { enabled: isSellarCredit },
  );

  const sellarCreditInvoiceIssuedAt =
    orderDocumentQuery.data?.pages[0]?.find((d) => d.type === "invoice")
      ?.issuedAt ?? null;

  // Fetch the Stripe invoice URL for unpaid buyer invoices without a payment link.
  const invoiceUrlQuery = useOrderInvoiceUrl(order.id, {
    enabled:
      !!order.stripeInvoiceId &&
      isBuyer &&
      !isSellarCredit &&
      !paymentLinkCode &&
      order.paymentStatus === "open",
  });

  // Don't show an invoice if cancelled
  if (order.cancelledAt) return null;

  // ─── Sellar Credit ───────────────────────────────────────────────────────────
  // No invoice is available until Sellar issues one post-dispatch.
  if (isSellarCredit) {
    if (!sellarCreditInvoiceIssuedAt) return null;
    return (
      <InvoiceStatusRow
        text={
          <>
            <span className="font-semibold">Invoice issued</span> on{" "}
            {dayjs(sellarCreditInvoiceIssuedAt).format(DATE_FORMAT)}
          </>
        }
        onView={() => {
          document
            .getElementById("orderDocuments")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
      />
    );
  }

  // Checkout orders can receive an invoiceIssuedAt timestamp when accepted, but
  // the supplier remains responsible for issuing the official VAT invoice.
  const isInvoiceManagedBySupplier =
    !order.invoiceIssuedAt || order.paymentType === "checkout";

  // ─── Invoice managed by supplier ─────────────────────────────────────────────
  if (isInvoiceManagedBySupplier) {
    return (
      <>
        <InvoiceStatusRow
          text={
            isSellarImport ? (
              <span className="font-semibold">Invoice managed by Sellar</span>
            ) : isBuyer ? (
              <span className="font-semibold">Invoice managed by supplier</span>
            ) : (
              <span className="font-semibold">Invoice managed offline</span>
            )
          }
          onView={() => setLearnMoreOpen(true)}
          ctaLabel="Learn more"
        />

        <Dialog
          active={learnMoreOpen}
          onDismiss={() => setLearnMoreOpen(false)}
        >
          <DialogNavigation
            leftIcon="CHEVRON_DOWN"
            onClickLeftIcon={() => setLearnMoreOpen(false)}
            title={
              isSellarImport
                ? "Invoice managed by Sellar"
                : isBuyer
                  ? "Invoice managed by supplier"
                  : "Invoice managed offline"
            }
          />
          <DialogContent className="flex flex-col gap-4 px-4 pb-4">
            {isSellarImport ? (
              <p>
                The VAT invoice for this order will be issued upon payment of
                your remaining balance for this order. The remaining balance
                will be taken automatically once the stock has arrived in
                Sellar&apos;s depot in the UK.
              </p>
            ) : (
              <>
                <BuyerOnly>
                  <p>
                    {order.Supplier.Business.name} will issue the VAT invoice
                    for this order outside of Sellar.{" "}
                    {showPaymentBeforeDispatchWarning && (
                      <>
                        They may request payment before your order is dispatched
                        unless you already have agreed credit terms with them.
                      </>
                    )}
                  </p>
                </BuyerOnly>
                <SupplierOnly>
                  <p>
                    A copy invoice will be made available on Sellar for this
                    order. However, you are responsible for issuing the official
                    VAT invoice for {order.Retailer.Business.name}.
                    {showPaymentBeforeDispatchWarning &&
                      !order.paidAt &&
                      order.Supplier.stripeAccountId && (
                        <>
                          {" "}
                          You can still request pro-forma payment through
                          Sellar. A link to pay by card will be sent to your
                          customer&apos;s email address and payments are made
                          directly to your connected Stripe account.
                        </>
                      )}
                  </p>
                </SupplierOnly>
              </>
            )}
          </DialogContent>
          <DialogCTA>
            <Button onClick={() => setLearnMoreOpen(false)}>Got it</Button>
          </DialogCTA>
        </Dialog>
      </>
    );
  }

  // ─── Invoice issued ─────────────────────────────────────────────────────────
  // Resolve the "view" action based on context.
  const issuedText = (
    <>
      <span className="font-semibold">Invoice issued</span> on{" "}
      {dayjs(order.invoiceIssuedAt).format(DATE_FORMAT)}
    </>
  );

  if (order.stripeInvoiceId && order.paymentStatus === "paid") {
    return <InvoiceStatusRow text={issuedText} onView={onVatInvoiceClick} />;
  }

  if (isBuyer && paymentLinkCode) {
    return (
      <InvoiceStatusRow
        text={issuedText}
        onView={() =>
          navigate(PAYMENT_LINK_REF.href({ code: paymentLinkCode }))
        }
      />
    );
  }

  if (isBuyer && order.stripeInvoiceId) {
    const invoiceUrl =
      invoiceUrlQuery.data?.invoicePdf ?? invoiceUrlQuery.data?.invoiceUrl;
    return (
      <InvoiceStatusRow
        text={issuedText}
        onView={
          invoiceUrl ? () => window.open(invoiceUrl, "_blank") : undefined
        }
      />
    );
  }

  if (isSupplier && order.stripeInvoiceId && order.Supplier.stripeAccountId) {
    const stripeUrl = buildStripeDashboardUrl({
      type: "invoice",
      stripeAccountId: order.Supplier.stripeAccountId,
      id: order.stripeInvoiceId,
    });
    return (
      <InvoiceStatusRow
        text={issuedText}
        onView={() => window.open(stripeUrl, "_blank")}
      />
    );
  }

  // Sellar-issued invoice: open the VAT invoice dialog.
  return <InvoiceStatusRow text={issuedText} onView={onVatInvoiceClick} />;
};
